<template>
    <div class="mr-3">
        <section class="w-[30rem] border border-surface rounded-lg mx-auto bg-surface-0 dark:bg-surface-950">
            <div class="flex w-full items-center justify-between px-1">
                <IconField icon-position="left" class="w-full">
                    <InputIcon class="pi pi-search text-surface-500 dark:text-surface-300" />
                    <InputText v-model="value" type="text"
                               @input="visible = true"
                               :placeholder="placeholder" class="w-full !border-0 !shadow-none !outline-0" @click="visible = true" />
                </IconField>
            </div>
        </section>
        <Dialog
            v-model:visible="visible"
            dismissable-mask
            modal
            :show-header="false"
            :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
            class="!w-[50vw] !max-w-2xl !overflow-hidden !rounded-xl dark:!bg-surface-950"
            content-class="!p-0"
            append-to="body"
            @hide="
                visible = false;
                value = '';
            "
        >
            <div class="flex w-full bg-surface-0 dark:bg-surface-950 items-center justify-between px-1 py-1.5">
                <IconField icon-position="left" class="w-full">
                    <InputIcon class="pi pi-search text-surface-500 dark:text-surface-300" />
                    <InputText v-model="value" type="text" :placeholder="placeholder" class="w-full !border-0 !shadow-none !outline-0" />
                </IconField>
                <LoadingState v-if="loading" small class="border p-1 border-surface rounded-border bg-surface-100 dark:bg-surface-700 mr-2 text-surface-600 dark:text-surface-200 text-xs block" />
            </div>
            <div class="border-y border-surface p-4">
                <h2 class="font-semibold text-xs text-surface-500 dark:text-surface-300 px-2 uppercase">WORKSPACES</h2>
                <ul class="mt-1">
                    <li
                        v-for="(item, index) of workspaces"
                        :key="index"
                        @click="gotoWorkspace(item)"
                        class="select-none p-2 font-medium text-base cursor-pointer rounded-lg hover:bg-emphasis hover:text-primary flex gap-2 items-center border border-transparent hover:border-surface"
                    >
                        <div class="flex flex-col justify-center">
                            <span class="text-surface-800 dark:text-surface-50 font-bold">{{ item.name }}</span>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="border-surface p-4">
                <h2 class="font-semibold text-xs text-surface-500 dark:text-surface-300 px-2 uppercase">GRAPHS</h2>
                <ul class="mt-1">
                    <li
                        v-for="(item, index) of graphs"
                        :key="index"
                        @click="gotoGraph(item)"
                        class="select-none p-2 font-medium text-base cursor-pointer rounded-lg hover:bg-emphasis hover:text-primary flex gap-2 items-center border border-transparent hover:border-surface"
                    >
                        <div class="flex justify-between w-full">
                            <span class="text-surface-800 dark:text-surface-50 font-bold">{{ item.name }}</span>
                            <span class="text-surface-800 dark:text-surface-950 text-sm">{{findWorkspaceName(item.workspace)}}</span>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="border-surface p-4 border-t" v-if="selectedGraphId">
                <h2 class="font-semibold text-xs text-surface-500 dark:text-surface-300 px-2 uppercase">GRAPH ELEMENTS (CURRENT GRAPH)</h2>
                <ul class="mt-1">
                    <li
                        v-for="(item, index) of graphElements"
                        :key="index"
                        @click="selectElement(item)"
                        class="select-none p-2 font-medium w-full text-base cursor-pointer rounded-lg hover:bg-emphasis hover:text-primary flex justify-between items-center border border-transparent hover:border-surface"
                    >
                        <div class="flex flex-col">
                            <span class="text-surface-800 dark:text-surface-50 font-bold">{{ item.value }}</span>
                        <span class="text-surface-800 dark:text-surface-950 text-sm">{{item.key}}</span>
                        </div>
                        <span class="text-surface-800 dark:text-surface-950 text-sm">{{type(item.ob)}}</span>
                    </li>
                </ul>
            </div>
        </Dialog>
    </div>
</template>
<script setup>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import {computed, ref, watch} from 'vue';
import {is} from '@/lib/util/ModelUtil';
import {useStore} from "vuex";
import _ from "lodash-es";
import {WORKSPACE_API_TYPE} from "@/src/model/apiTypes";

import {
    checkVisibility, getActivityName,
} from "@/lib/util/ModelUtil"
import {isAny} from "@/lib/features/modeling/util/ModelingUtil";

const store = useStore()

const visible = ref(false);
const value = ref('');

const workspaces = ref([])
const graphs = ref([])
const graphElements = ref([])

const search = async () => {
    if (!value.value) {
        workspaces.value = []
        graphs.value = []
        graphElements.value = []
        return
    }
    const data = await store.dispatch('repoStore/search', value.value)
    workspaces.value = data.workspaces
    graphs.value = data.graphs
    if (selectedGraphId.value) {
        graphElements.value = []
        searchInGraphDefinition()
    }
}
const graph = computed(() => store.getters["repoStore/getGraph"])
const shouldKeepResult = (key, ob) => {
    const excludedKeys = [
        'id',
        '$type'
    ]
    return !excludedKeys.includes(key)
}
const searchInGraphDefinition = () => {
    if (!graph.value) {
        return
    }
    const definition = modeler.value._definitions.rootElements[0].boardElements
    function searchObject(obj, term) {
        for (const key in obj) {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                searchObject(obj[key], term);
            } else if (typeof obj[key] === 'string' && obj[key].toLowerCase().includes(term.toLowerCase()) && shouldKeepResult(key, obj)) {
                graphElements.value.push({ key, value: obj[key], ob: obj });
            }
        }
    }

    searchObject(definition, value.value);
}

watch(value, _.debounce(search, 300))

const loading = computed(() => store.getters.isLoading(WORKSPACE_API_TYPE.SEARCH))

const gotoWorkspace = (item) => {
    store.dispatch('repoStore/setSelectedSpace', item.id)
    store.dispatch('repoStore/setSelectedGraph', null)
    visible.value = false
    value.value = ''
}

const gotoGraph = (item) => {
    store.dispatch('repoStore/setSelectedGraph', item)
    visible.value = false
    value.value = ''
}

const findWorkspaceName = id => {
    return store.getters["repoStore/graphTree"].find(ws => parseInt(ws.id) === parseInt(id))?.label
}

const selectedGraphId = computed(() => store.state.repoStore.selectedGraphId)

const placeholder = computed(() => {
    return `Search folders and graphs ${selectedGraphId.value ? 'or graph elements' : ''}`
})

const modeler = computed(() => store.getters["graphStore/getModeler"])
const selectElement = item =>  {
    let elId = item.ob.id
    if (isAny(item.ob, [
        'od:Duration',
        'od:Roles'
    ])) {
        elId = item.ob.$parent.id
    }
    // search in nested expression
    let parent = item.ob.$parent
    if (is(item.ob, 'od:Expression')) {
        while (is(parent, 'od:NestedExpression')) {
            parent = parent.ob ? parent.ob.$parent : parent.$parent
        }
        elId = parent.id
    }
    const element = modeler.value.get('elementRegistry').get(elId)
    modeler.value.get('selection').select([element])
    checkVisibility(modeler.value, element)
    visible.value = false
    value.value = ''
}

const type = (ob) => {
    const t = ob.$type.replace('od:', '')
    if (is(ob, 'od:Roles')) {
        let activityName = getActivityName(ob.$parent)
        return `${t} of ${activityName}`
    }

    if (is(ob, 'od:Duration')) {
        return `${t} of ${ob.$parent.$type.replace('od:', '')}`
    }

    if (is(ob, 'od:Expression')) {
        let parent = ob.$parent
        while (is(parent, 'od:NestedExpression')) {
            parent = parent.ob ? parent.ob.$parent : parent.$parent
        }
        return `${t} of ${parent.$type.replace('od:', '')}`
    }

    return t
}
</script>
